import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Archive, Edit, Percent, Unarchive } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api, { ApiResponse } from '../../services/api';
import { workspaceService, WorkspaceListItem } from '../../services/workspace.service';
import { User } from '../../services/users.service';
import { trackingService } from '../../services/tracking.service';
import { profitShareService } from '../../services/profit-share.service';
import { useAuthStore, WorkspaceRef } from '../../stores/auth.store';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import { resolveDatePreset } from '../../utils/date-presets';
import { dayRangeParams } from '../../utils/timezone';
import { errorMessage } from '../../utils/errorMessage';

const money = (v: number | undefined) => (v === undefined ? '—' : `$${Math.round(v).toLocaleString()}`);
const userLabel = (u: Pick<User, 'email' | 'first_name' | 'last_name'>) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;

export default function WorkspacesPanel() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const setUserWorkspaces = useAuthStore((s) => s.setUserWorkspaces);
  const { timezone } = useReportDefaults();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<WorkspaceListItem | 'new' | null>(null);
  const [archiving, setArchiving] = useState<WorkspaceListItem | null>(null);

  const list = useQuery({ queryKey: ['workspaces-admin'], queryFn: workspaceService.list });
  const { date_from, date_to } = useMemo(() => dayRangeParams(resolveDatePreset('last_7_days')), []);
  const activity = useQuery({
    queryKey: ['ws-overview', date_from, date_to, timezone],
    queryFn: () => trackingService.getWorkspaceOverview({ date_from, date_to, timezone }),
    staleTime: 5 * 60_000,
  });
  const statsById = new Map((activity.data?.rows ?? []).map((r) => [r.group_key, r]));

  // Selectors read the signed-in user's list, so a create, rename or archive must refresh it.
  const changed = async () => {
    await queryClient.invalidateQueries({ queryKey: ['workspaces-admin'] });
    await queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
    const me = await api.get<ApiResponse<{ workspaces?: WorkspaceRef[] }>>('/auth/me');
    setUserWorkspaces(me.data.data?.workspaces ?? []);
  };

  const rows = (list.data ?? []).filter((w) => showArchived || !w.archived_at);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Workspaces</Typography>
          <Typography variant="body2" color="text.secondary">Spend, revenue and profit over the last 7 days.</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label={<Typography variant="body2">Show archived</Typography>} />
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setEditing('new')}>New workspace</Button>
        </Box>
      </Box>

      {list.isLoading ? <CircularProgress size={24} /> : list.isError ? (
        <Alert severity="error">Could not load workspaces.</Alert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Active flows</TableCell>
                <TableCell align="right">Spend</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Profit</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((w) => {
                const s = statsById.get(w.id);
                return (
                  <TableRow key={w.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {w.name}
                        {w.archived_at && <Chip size="small" label="Archived" />}
                        {w.share_pct != null && (
                          <Chip size="small" color="primary" variant="outlined" label={`Share ${w.share_pct}%`}
                            onClick={() => navigate(`/profit-share/${w.id}`)} />
                        )}
                        {!w.archived_at && !w.has_fallback && (
                          <Tooltip title="No fallback group: traffic no flow can serve gets a blank page. Set it under Settings → Workspace.">
                            <Chip size="small" color="warning" variant="outlined" label="No fallback" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{w.member_count}</TableCell>
                    <TableCell align="right">{w.active_flows}</TableCell>
                    <TableCell align="right">{money(s?.cost)}</TableCell>
                    <TableCell align="right">{money(s?.revenue)}</TableCell>
                    <TableCell align="right" sx={{ color: (s?.profit ?? 0) < 0 ? 'error.main' : undefined }}>{money(s?.profit)}</TableCell>
                    <TableCell>{new Date(w.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Profit share">
                        <IconButton size="small" aria-label={`Profit share of ${w.name}`} onClick={() => navigate(`/profit-share/${w.id}`)}><Percent fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${w.name}`} onClick={() => setEditing(w)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={w.archived_at ? 'Unarchive' : 'Archive'}>
                        <IconButton size="small" aria-label={`${w.archived_at ? 'Unarchive' : 'Archive'} ${w.name}`} onClick={() => setArchiving(w)}>
                          {w.archived_at ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {editing && (
        <EditWorkspaceDialog
          workspace={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async ({ message, variant }) => { setEditing(null); enqueueSnackbar(message, { variant }); await changed(); }}
        />
      )}
      {archiving && (
        <ArchiveDialog
          workspace={archiving}
          onClose={() => setArchiving(null)}
          onDone={async (msg) => { setArchiving(null); enqueueSnackbar(msg, { variant: 'success' }); await changed(); }}
        />
      )}
    </Box>
  );
}

function EditWorkspaceDialog({ workspace, onClose, onSaved }: {
  workspace: WorkspaceListItem | null;
  onClose: () => void;
  onSaved: (result: { message: string; variant: 'success' | 'warning' }) => void;
}) {
  const [name, setName] = useState(workspace?.name ?? '');
  const [share, setShare] = useState('');
  const shareValue = share.trim() === '' ? null : Number(share);
  const shareInvalid = shareValue !== null && !(Number.isFinite(shareValue) && shareValue >= 0 && shareValue <= 100);
  const [memberIds, setMemberIds] = useState<string[] | null>(workspace ? null : []);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users-for-members'],
    // Every active user, whatever the toolbar's workspace filter shows.
    queryFn: async () => (await api.post<ApiResponse<User[]>>('/users/query',
      { where: { user: { status: 'active' } }, limit: 2000, order_by: 'user.email', order: 'asc' },
      { params: { workspaceIds: '' } })).data.data!.filter((u) => u.role !== 'admin' && !u.partner_id),
  });
  const members = useQuery({
    queryKey: ['workspace-members', workspace?.id],
    queryFn: () => workspaceService.members(workspace!.id),
    enabled: !!workspace,
    // The edit replaces the whole set, so it must start from the server's, never a cached one.
    staleTime: 0,
  });
  const fixed = (members.data ?? []).filter((m) => m.partner_id);
  const selected = memberIds ?? (members.data ?? []).filter((m) => !m.partner_id && m.role !== 'admin').map((m) => m.id);

  const save = useMutation({
    mutationFn: async () => {
      if (!workspace) {
        const created = await workspaceService.create({ name, member_ids: selected });
        if (shareValue === null) return { message: `Created ${name.trim()}`, variant: 'success' as const };
        try {
          await profitShareService.setTerms({ workspace_id: created.id, effective_month: new Date().toISOString().slice(0, 7), share_pct: shareValue });
        } catch (err) {
          // The workspace exists by now: a warning, not an error that invites a second Create.
          return { message: `Created ${name.trim()}, but its profit share wasn't set (${errorMessage(err, 'unknown error')}). Set it on its Profit Share page.`, variant: 'warning' as const };
        }
        return { message: `Created ${name.trim()} with a ${shareValue}% profit share`, variant: 'success' as const };
      }
      if (name.trim() !== workspace.name) await workspaceService.updateWorkspace(workspace.id, { name });
      if (memberIds) await workspaceService.setMembers(workspace.id, memberIds);
      return { message: `Saved ${name.trim()}`, variant: 'success' as const };
    },
    onSuccess: onSaved,
    onError: (err) => setError(errorMessage(err, 'Could not save the workspace')),
  });

  const options = users.data ?? [];
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{workspace ? `Edit ${workspace.name}` : 'New workspace'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Name" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        <Autocomplete
          multiple
          size="small"
          loading={users.isLoading || members.isLoading}
          options={options.map((u) => u.id)}
          value={selected}
          onChange={(_, ids) => setMemberIds(ids)}
          getOptionLabel={(uid) => {
            const u = options.find((o) => o.id === uid) ?? members.data?.find((m) => m.id === uid);
            return u ? userLabel(u) : uid;
          }}
          renderInput={(params) => (
            <TextField {...params} label="Members" helperText="Admins reach every workspace by role and aren't listed." />
          )}
        />
        {!workspace && (
          <TextField label="Profit share %" size="small" value={share} onChange={(e) => setShare(e.target.value)}
            type="number" inputProps={{ min: 0, max: 100, step: 0.01 }} error={shareInvalid}
            helperText={shareInvalid ? 'A number from 0 to 100' : 'Optional. From this month; change it later on its Profit Share page.'} />
        )}
        {fixed.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Partner users (managed with their partner): {fixed.map(userLabel).join(', ')}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => { setError(null); save.mutate(); }}
          disabled={!name.trim() || shareInvalid || save.isPending || (!!workspace && members.isFetching)}>
          {save.isPending ? 'Saving…' : workspace ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ArchiveDialog({ workspace, onClose, onDone }: {
  workspace: WorkspaceListItem;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const archived = !!workspace.archived_at;
  const run = useMutation({
    mutationFn: () => (archived ? workspaceService.unarchive(workspace.id) : workspaceService.archive(workspace.id)),
    onSuccess: () => onDone(archived ? `Unarchived ${workspace.name}` : `Archived ${workspace.name}`),
    onError: (err) => setError(errorMessage(err, archived ? 'Could not unarchive' : 'Could not archive')),
  });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{archived ? `Unarchive ${workspace.name}?` : `Archive ${workspace.name}?`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {archived
            ? 'It returns to every workspace selector and accepts new entities again.'
            : 'It leaves every workspace selector and accepts no new entities. Its data and past reports stay. A workspace with active flows, clicks in the last 24 hours or spend in the last 2 days can’t be archived.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={archived ? 'primary' : 'warning'} onClick={() => { setError(null); run.mutate(); }} disabled={run.isPending}>
          {run.isPending ? '…' : archived ? 'Unarchive' : 'Archive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
